// Se importa como `Mapa`: llamarlo `Map` sombrearía el Map nativo de
// JavaScript en este archivo.
import { useEffect, useRef } from 'react';
import Mapa, { Marker, NavigationControl, type MapRef } from 'react-map-gl/maplibre';
import { MapPin } from 'lucide-react';
import 'maplibre-gl/dist/maplibre-gl.css';
import { CITY, type LatLng } from '@kick/types';

/**
 * Elige un punto en el mapa.
 *
 * Se usa en el alta de comercios, donde la ubicación no es un adorno: de ese
 * punto sale la distancia con la que se cobra cada domicilio. Escribir la
 * dirección no basta —«Calle 50 #45-20» son varias cuadras—, así que hay que
 * marcarla.
 */
export function SelectorPunto({
  valor,
  onCambio,
  altura = 'h-64',
}: {
  valor: LatLng | null;
  onCambio: (punto: LatLng) => void;
  altura?: string;
}) {
  const punto = valor ?? CITY.center;
  const mapa = useRef<MapRef>(null);

  /** Cuando el punto cambia desde fuera (la dirección se geocodificó), el mapa lo sigue. */
  useEffect(() => {
    if (!valor) return;
    mapa.current?.easeTo({ center: [valor.lng, valor.lat], zoom: 16, duration: 600 });
    // Solo al cambiar las coordenadas, no en cada render.
  }, [valor?.lat, valor?.lng]);

  return (
    <div>
      <div className={`${altura} overflow-hidden rounded-kick border border-kick-gray/20`}>
        <Mapa
          ref={mapa}
          initialViewState={{ longitude: punto.lng, latitude: punto.lat, zoom: 13 }}
          mapStyle="/map-style.json"
          style={{ width: '100%', height: '100%' }}
          // Hacer clic mueve el marcador: más rápido que arrastrarlo desde lejos.
          onClick={(e) => onCambio({ lat: e.lngLat.lat, lng: e.lngLat.lng })}
        >
          <NavigationControl position="top-right" showCompass={false} />
          {valor && (
            <Marker
              longitude={valor.lng}
              latitude={valor.lat}
              draggable
              onDragEnd={(e) => onCambio({ lat: e.lngLat.lat, lng: e.lngLat.lng })}
            >
              <MapPin className="h-8 w-8 -translate-y-4 fill-kick-red text-white drop-shadow" />
            </Marker>
          )}
        </Mapa>
      </div>

      <p className="mt-1.5 font-mono text-xs text-kick-gray">
        {valor
          ? `${valor.lat.toFixed(6)}, ${valor.lng.toFixed(6)}`
          : 'Haz clic en el mapa para marcar la ubicación'}
      </p>
    </div>
  );
}
